/*
 * Cart page helpers for sections/custom-cart-suggestions.liquid and the
 * cart-page copy of sections/custom-guide-links.liquid.
 *
 * <custom-cart-refresh data-section-id> wraps a section whose content depends
 * on the cart: the suggestion shelf, and the guide links that show only while
 * the cart is empty. After every cartUpdate publish, one Section Rendering
 * request (/cart?sections=…) re-renders all of them and swaps them in, so
 * removing the last item brings up the guide and adding one takes it away.
 *
 * <custom-cart-recommendations data-urls="a|b"> tries each recommendations
 * URL in order (complementary, then related) and keeps the first that
 * returns products; if none does, the shelf hides itself.
 *
 * <custom-recently-viewed-shelf> wraps the shelf's content. Below free
 * shipping (data-mode "small") it moves the viewed products that reach it
 * (custom-recently-viewed.js) to the front of the gap list. The Recently
 * viewed list itself sits above the shelf (<custom-recently-viewed-grid>).
 *
 * [data-cart-suggestion-add] buttons add one of that variant through
 * /cart/add.js with the sections cart.js renders, paint them the same way
 * (as custom-cart-gift.js does) and publish cartUpdate with source
 * 'cart-items' so <cart-items> does not refetch on top of it.
 * Adding to an empty cart reloads the page instead. Either way the page
 * returns to its top, with focus on the cart heading.
 *
 * Both sections include this script, so everything is guarded against a
 * second run.
 */
if (!window.customCartSuggestions) {
  window.customCartSuggestions = true;

  let refreshTimer;

  const refreshSections = () => {
    const elements = Array.from(document.querySelectorAll('custom-cart-refresh[data-section-id]'));
    if (!elements.length) return;
    const ids = [...new Set(elements.map((element) => element.dataset.sectionId))];

    fetch(`${routes.cart_url}?sections=${ids.join(',')}`)
      .then((response) => response.json())
      .then((sections) => {
        elements.forEach((element) => {
          const html = sections[element.dataset.sectionId];
          if (!html) return;
          const fresh = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector(`custom-cart-refresh[data-section-id="${element.dataset.sectionId}"]`);
          if (fresh) element.replaceWith(fresh);
        });
      })
      .catch((error) => console.error(error));
  };

  document.addEventListener('DOMContentLoaded', () => {
    if (typeof subscribe !== 'function') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(refreshSections, 150);
    });
  });

  class CustomCartRefresh extends HTMLElement {}
  customElements.define('custom-cart-refresh', CustomCartRefresh);

  class CustomCartRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;
      this.loaded = true;
      this.load((this.dataset.urls || '').split('|').filter(Boolean));
    }

    load(urls) {
      const url = urls.shift();
      if (!url) {
        // Hidden, not removed: the recently viewed shelf may still fill it.
        const shelf = this.closest('.cart-suggestions');
        if (shelf) shelf.hidden = true;
        return;
      }

      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const result = new DOMParser().parseFromString(text, 'text/html').querySelector('.cart-suggestions__result');
          if (!result || Number(result.dataset.count) === 0) {
            this.load(urls);
            return;
          }
          this.innerHTML = result.innerHTML;
          this.classList.remove('cart-suggestions__pending');
        })
        .catch(() => this.load(urls));
    }
  }
  customElements.define('custom-cart-recommendations', CustomCartRecommendations);

  // Below free shipping, the viewed products that reach it move to the front
  // of the gap list; the list keeps its length and its View all tile.
  class CustomRecentlyViewedShelf extends HTMLElement {
    connectedCallback() {
      if (this.started || !window.customRecentlyViewed || this.dataset.mode !== 'small') return;
      this.started = true;
      window.customRecentlyViewed
        .cards({ root: this.dataset.root, exclude: this.dataset.inCart })
        .then((cards) => {
          if (this.isConnected) this.leadGapList(cards);
        });
    }

    leadGapList(cards) {
      const gap = Number(this.dataset.gap) || 0;
      const list = this.querySelector('.cart-suggestions__list');
      const lead = cards.filter((item) => Number(item.dataset.price) >= gap && Number(item.dataset.price) > 0);
      if (!list || !lead.length) return;
      const places = list.querySelectorAll('li[data-product-id]').length;
      const leadIds = new Set(lead.map((item) => item.dataset.productId));
      list.querySelectorAll('li[data-product-id]').forEach((item) => {
        if (leadIds.has(item.dataset.productId)) item.remove();
      });
      list.prepend(...lead.slice(0, places));
      Array.from(list.querySelectorAll('li[data-product-id]'))
        .slice(places)
        .forEach((item) => item.remove());
    }
  }
  customElements.define('custom-recently-viewed-shelf', CustomRecentlyViewedShelf);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-suggestion-add]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;

    const cartItems = document.querySelector('cart-items');
    if (!cartItems) return;

    const sections = cartItems.getSectionsToRender();
    // The empty cart page renders more than cart.js repaints (is-empty classes,
    // a disabled checkout button outside .js-contents), so the first item
    // added there reloads the page instead.
    const wasEmpty = cartItems.classList.contains('is-empty');
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');

    fetch(routes.cart_add_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        items: [{ id: Number(button.dataset.cartSuggestionAdd), quantity: 1 }],
        sections: sections.map((section) => section.section),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((state) => {
        if (state.status) throw new Error(state.description || state.message);
        if (wasEmpty) {
          // Reload at the top, not at the restored scroll position down by the shelf.
          history.scrollRestoration = 'manual';
          window.scrollTo(0, 0);
          window.location.reload();
          return;
        }
        sections.forEach((section) => {
          const target = document.getElementById(section.id);
          if (!target || !state.sections?.[section.section]) return;
          const element = target.querySelector(section.selector) || target;
          element.innerHTML = cartItems.getSectionInnerHTML(state.sections[section.section], section.selector);
        });
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });

        // Back to the top where the new line and the totals are; focus follows
        // so keyboard and screen reader users land there too.
        const title = document.querySelector('cart-items .title');
        if (title) {
          title.setAttribute('tabindex', '-1');
          title.focus({ preventScroll: true });
        }
        // Instant: a smooth scroll is cut short by the shelf re-render that follows.
        window.scrollTo(0, 0);
      })
      .catch((error) => {
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        const errors = document.getElementById('cart-errors');
        if (errors) errors.textContent = error.message || window.cartStrings.error;
      });
  });
}
